import { useState } from 'react'
import html2pdf from 'html2pdf.js'
import { WHEN, TYPES, TYPE_LABEL, filterCompliance, sortCompliance, groupByVessel, counts, whenOf, statusText, complianceTable } from '../lib/fleetCompliance'
import { buildComplianceHtml } from '../lib/fleetReport'
import { sectionsFile, exportName } from '../lib/exportCore'

// Fleet -> Compliance (fleet-2): surveys, ship and crew certificates, open ISM findings, drills, RA and SOP reviews that are overdue or
// due within 90 days on every vessel. Grouped by vessel (worst readiness first) or one list; filter by when / type; sortable;
// Export = the rows shown, as Excel or a Hey Captain PDF. items come from the Fleet page (src/lib/fleetCompliance.js).
const CSS = C => `
.hcc-bar{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:12px}
.hcc-chip{border:1px solid ${C.line};background:${C.card};color:${C.muted};border-radius:18px;padding:7px 13px;font-weight:600;font-size:12px;line-height:1;font-family:inherit;cursor:pointer;white-space:nowrap}
.hcc-chip.on{background:${C.blue};border-color:${C.blue};color:#fff}
.hcc-chip em{font-style:normal;margin-left:6px;opacity:.85}
.hcc-sel{background:${C.card};color:${C.text};border:1px solid ${C.line};border-radius:9px;padding:7px 10px;font-weight:600;font-size:12px;line-height:1;font-family:inherit}
.hcc-kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;margin-bottom:14px}
.hcc-k{background:${C.card};border:1px solid ${C.line};border-radius:12px;padding:11px 14px;cursor:pointer}
.hcc-k b{display:block;font-size:22px;font-variant-numeric:tabular-nums}
.hcc-k span{font-size:11.5px;color:${C.muted}}
.hcc-card{background:${C.card};border:1px solid ${C.line};border-radius:14px;padding:6px 14px 12px}
.hcc-row{display:grid;grid-template-columns:var(--cols);gap:10px;align-items:center;padding:9px 6px;border-top:1px solid ${C.line};font-size:12.5px}
.hcc-head{border-top:none;font-size:11px;color:${C.muted};font-weight:700;padding-top:10px}
.hcc-head button{background:none;border:none;color:inherit;font:inherit;cursor:pointer;padding:0;text-align:left}
.hcc-head button.on{color:${C.text}}
.hcc-gh{display:flex;align-items:center;gap:10px;padding:12px 6px 6px;font-weight:700;font-size:13.5px;border-top:1px solid ${C.line}}
.hcc-gh:first-of-type{border-top:none}
.hcc-gh span{font-size:11.5px;font-weight:600;color:${C.muted}}
.hcc-gh button{margin-left:auto;background:transparent;border:1px solid ${C.line};color:${C.muted};border-radius:8px;padding:5px 10px;font-weight:600;font-size:11.5px;line-height:1;font-family:inherit;cursor:pointer}
.hcc-name{font-weight:600;overflow-wrap:anywhere}
.hcc-sub{font-size:11.5px;color:${C.muted};margin-top:2px;overflow-wrap:anywhere}
.hcc-pill{display:inline-block;font-size:11px;font-weight:700;padding:3px 9px;border-radius:11px;white-space:nowrap}
.hcc-exp{position:relative;margin-left:auto}
.hcc-menu{position:absolute;right:0;top:calc(100% + 4px);z-index:20;background:${C.card};border:1px solid ${C.line};border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,.4);overflow:hidden;min-width:170px}
.hcc-menu button{display:block;width:100%;text-align:left;background:none;border:none;color:${C.text};padding:10px 14px;font-weight:600;font-size:12.5px;line-height:1;font-family:inherit;cursor:pointer}
.hcc-menu button:hover{background:rgba(59,123,255,.14)}
.hcc-m{display:none}
@media (max-width:1023px){.hcc-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media (max-width:700px){.hcc-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.hcc-head{display:none}.hcc-row{grid-template-columns:minmax(0,1fr) auto!important;row-gap:4px}.hcc-d{display:none}.hcc-m{display:block}.hcc-exp{margin-left:0}}
`

export default function FleetCompliance({ items, fleet, health, company, palette: C, onOpen, loading }) {
  const [when, setWhen] = useState('all')
  const [type, setType] = useState('all')
  const [sort, setSort] = useState({ key: 'days', dir: 1 })
  const [grouped, setGrouped] = useState(true)
  const [menu, setMenu] = useState(false)
  const [busy, setBusy] = useState('')
  const rank = Object.fromEntries((fleet || []).map((v, i) => [v.id, i]))
  const shown = sortCompliance(filterCompliance(items, { when, type }), sort.key, sort.dir, rank)
  const groups = groupByVessel(shown, fleet, health)
  const all = counts(items), c30 = (items || []).filter(i => i.status === 'due' && i.days <= 30).length
  const whenLabel = (WHEN.find(w => String(w[0]) === String(when)) || WHEN[0])[1] + (type === 'all' ? '' : ' · ' + TYPE_LABEL[type])
  const by = key => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))
  const tone = it => (it.status === 'overdue' ? C.red : it.status === 'open' ? C.blue : it.days <= 30 ? C.amber : '#8a6d1f')
  const COLS = grouped ? '120px minmax(0,1.6fr) minmax(0,1fr) 96px 120px' : 'minmax(0,.9fr) 120px minmax(0,1.6fr) minmax(0,1fr) 96px 120px'

  async function exportAs(fmt) {
    setMenu(false); setBusy(fmt)
    try {
      if (!shown.length) throw new Error('Nothing to export - no item in this filter.')
      const title = 'Fleet compliance - ' + whenLabel
      if (fmt === 'xlsx') {
        const file = await sectionsFile([{ name: 'Fleet compliance', table: complianceTable(sortCompliance(shown, grouped ? 'vessel' : sort.key, grouped ? 1 : sort.dir, rank)) }], 'xlsx')
        const url = URL.createObjectURL(new Blob([file.bytes], { type: file.mime }))
        const a = document.createElement('a'); a.href = url; a.download = exportName(title, 'xlsx'); document.body.appendChild(a); a.click(); a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 4000)
      } else {
        const el = document.createElement('div')
        el.style.position = 'fixed'; el.style.left = '-10000px'; el.style.top = '0'
        el.innerHTML = buildComplianceHtml({ company, groups, filterLabel: whenLabel, generated: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) })
        document.body.appendChild(el)
        const opt = { margin: 8, filename: exportName(title, 'pdf'), image: { type: 'jpeg', quality: 0.96 }, html2canvas: { scale: 2, useCORS: true, windowWidth: 960, width: 960 }, pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }
        try { await html2pdf().set(opt).from(el.firstElementChild).save() } finally { document.body.removeChild(el) }
      }
    } catch (e) { alert('Could not export: ' + (e.message || e)) } finally { setBusy('') }
  }

  const row = (it, k) => (
    <div key={k} className="hcc-row" style={{ '--cols': COLS }} data-status={it.status} onClick={() => onOpen && onOpen(it.vid)} title={'Open ' + it.vessel}>
      {!grouped && <div className="hcc-d hcc-name">{it.vessel}</div>}
      <div className="hcc-d" style={{ color: C.muted }}>{it.typeLabel}</div>
      <div style={{ minWidth: 0 }}>
        <div className="hcc-name">{it.name}</div>
        <div className="hcc-sub hcc-m">{[!grouped && it.vessel, it.typeLabel, it.detail].filter(Boolean).join(' · ')}</div>
      </div>
      <div className="hcc-d hcc-sub" style={{ marginTop: 0 }}>{it.detail || '—'}</div>
      <div className="hcc-d" style={{ color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{it.date || '—'}</div>
      <div style={{ textAlign: 'right' }}>
        <span className="hcc-pill" style={{ background: tone(it) + '26', color: tone(it) }}>{statusText(it)}</span>
        <div className="hcc-sub" style={{ textAlign: 'right' }}>{whenOf(it)}</div>
      </div>
    </div>
  )
  const head = (
    <div className="hcc-row hcc-head" style={{ '--cols': COLS }}>
      {[...(grouped ? [] : [['vessel', 'Vessel']]), ['type', 'Type'], ['name', 'Item'], [null, 'Detail'], ['days', 'Due'], ['status', 'Status']].map(([k, l]) => (
        <div key={l} style={l === 'Status' ? { textAlign: 'right' } : null}>{k ? <button className={sort.key === k ? 'on' : ''} onClick={() => by(k)}>{l}{sort.key === k ? (sort.dir > 0 ? ' ↑' : ' ↓') : ''}</button> : l}</div>
      ))}
    </div>
  )

  return (
    <div>
      <style>{CSS(C)}</style>
      <div className="hcc-kpis">
        {[['overdue', all.overdue, 'Overdue / expired', C.red], [30, c30, 'Due ≤ 30 days', C.amber], [60, all.d60, 'Due in 31-60 days', '#8a6d1f'], [90, all.d90, 'Due in 61-90 days', C.muted], ['all', all.open, 'Open ISM, no due date', C.blue]].map(([w, n, l, col]) => (
          <div key={l} className="hcc-k" onClick={() => setWhen(w)} role="button"><b style={{ color: loading ? C.faint : n ? col : C.green }}>{loading ? '…' : n}</b><span>{l}</span></div>
        ))}
      </div>
      <div className="hcc-bar">
        {WHEN.map(([w, l]) => <button key={w} className={'hcc-chip' + (String(when) === String(w) ? ' on' : '')} onClick={() => setWhen(w)}>{l}<em>{filterCompliance(items, { when: w, type }).length}</em></button>)}
        <select className="hcc-sel" value={type} onChange={e => setType(e.target.value)} aria-label="Type">
          <option value="all">All types</option>
          {TYPES.map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
        </select>
        <button className={'hcc-chip' + (grouped ? ' on' : '')} onClick={() => setGrouped(g => !g)} title="Group the list by vessel">By vessel</button>
        <span className="hcc-exp">
          <button className="hcc-chip" onClick={() => setMenu(m => !m)} disabled={!!busy}>{busy ? 'Exporting…' : 'Export ▾'}</button>
          {menu && <div className="hcc-menu" role="menu"><button role="menuitem" onClick={() => exportAs('xlsx')}>Excel (.xlsx)</button><button role="menuitem" onClick={() => exportAs('pdf')}>PDF (Hey Captain)</button></div>}
        </span>
      </div>
      <div className="hcc-card">
        {loading ? <div style={{ color: C.muted, fontSize: 12.5, padding: '16px 6px' }}>Checking every vessel&hellip;</div>
          : !shown.length ? <div style={{ color: C.muted, fontSize: 12.5, padding: '16px 6px' }}>Nothing {when === 'overdue' ? 'overdue' : 'due'} in this filter. All clear.</div>
          : grouped ? groups.map(g => (
            <div key={g.vid} data-group={g.vid}>
              <div className="hcc-gh">{g.vessel}<span>{g.overdue} overdue &middot; {g.due} due{g.open ? ' · ' + g.open + ' open' : ''}</span>{onOpen && <button onClick={() => onOpen(g.vid)}>Open &rarr;</button>}</div>
              {head}
              {g.items.map((it, i) => row(it, g.vid + '-' + i))}
            </div>
          )) : <>{head}{shown.map((it, i) => row(it, i))}</>}
      </div>
    </div>
  )
}
